import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import api from '../utils/api'

export const useUsers = (params = {}) => {
  const { page = 1, limit = 10, search = '' } = params

  return useQuery(
    ['users', { page, limit, search }],
    async () => {
      const response = await api.get('/auth/users', {
        params: { page, limit, search }
      })
      return response.data
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (userData) => {
      const response = await api.post('/auth/users', userData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
      },
    }
  )
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ id, ...userData }) => {
      const response = await api.put(`/auth/users/${id}`, userData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
      },
    }
  )
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (userId) => {
      const response = await api.delete(`/auth/users/${userId}`)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
      },
    }
  )
}

// Hook compuesto para manejar todas las operaciones de usuarios
export const useUserManagement = (initialParams = {}) => {
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    ...initialParams
  })

  const usersQuery = useUsers(params)
  const createUserMutation = useCreateUser()
  const updateUserMutation = useUpdateUser()
  const deleteUserMutation = useDeleteUser()

  const handleSearch = useCallback((search) => {
    setParams(prev => ({ ...prev, search, page: 1 }))
  }, [])

  const handlePageChange = useCallback((page) => {
    setParams(prev => ({ ...prev, page }))
  }, [])

  const handleLimitChange = useCallback((limit) => {
    setParams(prev => ({ ...prev, limit, page: 1 }))
  }, [])

  const createUser = useCallback(async (userData) => {
    return createUserMutation.mutateAsync(userData)
  }, [createUserMutation])

  const updateUser = useCallback(async (id, userData) => {
    return updateUserMutation.mutateAsync({ id, ...userData })
  }, [updateUserMutation])

  const deleteUser = useCallback(async (userId) => {
    return deleteUserMutation.mutateAsync(userId)
  }, [deleteUserMutation])

  return {
    // Data
    users: usersQuery.data?.users || [],
    pagination: usersQuery.data?.pagination || {},
    
    // Loading states
    isLoading: usersQuery.isLoading,
    isCreating: createUserMutation.isLoading,
    isUpdating: updateUserMutation.isLoading,
    isDeleting: deleteUserMutation.isLoading,
    
    // Error states
    error: usersQuery.error,
    createError: createUserMutation.error,
    updateError: updateUserMutation.error,
    deleteError: deleteUserMutation.error,
    
    // Actions
    handleSearch,
    handlePageChange,
    handleLimitChange,
    createUser,
    updateUser,
    deleteUser,
    
    // Params
    params,
    setParams,
    
    // Refetch
    refetch: usersQuery.refetch
  }
}

export default useUserManagement
